import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Autocomplete,
  TextField,
  Chip,
  Checkbox,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  IndeterminateCheckBox as PartialIcon
} from '@mui/icons-material';

// Hierarchical option structure
interface HierarchicalOption {
  value: string;
  label: string;
  children?: HierarchicalOption[];
  level?: number;
  parentValue?: string;
}

// Flat option structure (for backward compatibility)
interface FlatOption {
  value: string;
  label: string;
}

type FilterOption = FlatOption | HierarchicalOption;

// Format chip label with title case and special overrides
const formatChipLabel = (label: string, value?: string): string => {
  // Special overrides based on value
  if (value === 'cake_pos') {
    return 'CAKE POS';
  }
  if (value === 'olo_third_party') {
    return 'OLO 3rd Party';
  }
  if (value === 'dinein' || value === 'dine_in' || label.toLowerCase() === 'dinein') {
    return 'Dine In';
  }
  
  // Title case: capitalize first letter of each word
  return label
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

interface UnifiedFilterComponentProps {
  title: string;
  options: FilterOption[];
  value?: string[];
  defaultValue?: string[];
  placeholder?: string;
  onChange?: (values: string[]) => void;
  disabled?: boolean;
  allowSelectAll?: boolean;
  // Hierarchical specific props
  hierarchical?: boolean;
  maxSelection?: number; // Maximum number of items that can be selected
  defaultExpanded?: boolean;
  showHierarchyInAutocomplete?: boolean;
}

export const UnifiedFilterComponent: React.FC<UnifiedFilterComponentProps> = ({
  title,
  options,
  value,
  defaultValue,
  placeholder = 'Select options...',
  onChange,
  disabled = false,
  allowSelectAll = true,
  hierarchical = false,
  maxSelection,
  defaultExpanded = false,
  showHierarchyInAutocomplete = false
}) => {
  const [selectedValues, setSelectedValues] = useState<string[]>(value || defaultValue || []);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Initialize expansion state
  React.useEffect(() => {
    if (hierarchical && defaultExpanded) {
      const allParentValues = new Set<string>();
      const findParents = (opts: FilterOption[]) => {
        opts.forEach(opt => {
          if ('children' in opt && opt.children?.length) {
            allParentValues.add(opt.value);
            findParents(opt.children);
          }
        });
      };
      findParents(options);
      setExpandedNodes(allParentValues);
    }
  }, [hierarchical, defaultExpanded, options]);

  // Sync with value changes (controlled component)
  React.useEffect(() => {
    if (value !== undefined) {
      setSelectedValues(value);
    }
  }, [value]);

  // Sync with defaultValue changes (uncontrolled component)
  React.useEffect(() => {
    if (value === undefined && defaultValue) {
      setSelectedValues(defaultValue);
    }
  }, [defaultValue, value]);



  // Utility functions for hierarchical handling
  const flattenOptions = (opts: FilterOption[], level = 0, parentValue?: string): HierarchicalOption[] => {
    const flattened: HierarchicalOption[] = [];
    opts.forEach(opt => {
      const hierarchicalOpt: HierarchicalOption = {
        ...opt,
        level,
        parentValue,
        children: 'children' in opt ? opt.children : undefined
      };
      flattened.push(hierarchicalOpt);
      
      if ('children' in opt && opt.children?.length) {
        flattened.push(...flattenOptions(opt.children, level + 1, opt.value));
      }
    });
    return flattened;
  };

  const flatOptions = React.useMemo(() => {
    return hierarchical ? flattenOptions(options) : options as HierarchicalOption[];
  }, [options, hierarchical]);

  const getChildValues = (parentValue: string): string[] => {
    const childValues: string[] = [];
    const findChildren = (opts: FilterOption[]) => {
      opts.forEach(opt => {
        if ('children' in opt && opt.value === parentValue && opt.children) {
          opt.children.forEach(child => {
            childValues.push(child.value);
            if ('children' in child && child.children) {
              childValues.push(...getChildValues(child.value));
            }
          });
        } else if ('children' in opt && opt.children) {
          findChildren(opt.children);
        }
      });
    };
    findChildren(options);
    return childValues;
  };

  const getParentValue = (childValue: string): string | undefined => {
    const findParent = (opts: FilterOption[], parent?: string): string | undefined => {
      for (const opt of opts) {
        if (opt.value === childValue) {
          return parent;
        }
        if ('children' in opt && opt.children) {
          const result = findParent(opt.children, opt.value);
          if (result) return result;
        }
      }
      return undefined;
    };
    return findParent(options);
  };

  const getSelectionState = (optionValue: string): 'none' | 'partial' | 'full' => {
    if (!hierarchical) {
      return selectedValues.includes(optionValue) ? 'full' : 'none';
    }

    const childValues = getChildValues(optionValue);
    if (childValues.length === 0) {
      // Leaf node
      return selectedValues.includes(optionValue) ? 'full' : 'none';
    }

    // Parent node
    const selectedChildCount = childValues.filter(child => selectedValues.includes(child)).length;
    if (selectedChildCount === 0) return 'none';
    if (selectedChildCount === childValues.length) return 'full';
    return 'partial';
  };

  const handleChange = (newValues: string[]) => {
    // Enforce maxSelection limit if specified
    let finalValues = newValues;
    if (maxSelection && newValues.length > maxSelection) {
      // Keep the most recently selected items up to the limit
      finalValues = newValues.slice(-maxSelection);
    }
    
    // For hierarchical filters, filter out parent regions that weren't explicitly selected
    // (i.e., remove parents unless all their children are selected)
    if (hierarchical) {
      const valuesToCheck = [...finalValues]; // Create a copy for checking
      finalValues = finalValues.filter(value => {
        const childValues = getChildValues(value);
        // If it has children, it's a parent - only keep if all children are selected
        if (childValues.length > 0) {
          const allChildrenSelected = childValues.every(child => valuesToCheck.includes(child));
          return allChildrenSelected;
        }
        // Leaf nodes are always kept
        return true;
      });
    }
    
    setSelectedValues(finalValues);
    if (onChange) {
      onChange(finalValues);
    }
  };

  const handleHierarchicalToggle = (optionValue: string) => {
    if (!hierarchical) {
      // Fallback to regular toggle for non-hierarchical
      const newValues = selectedValues.includes(optionValue)
        ? selectedValues.filter(v => v !== optionValue)
        : [...selectedValues, optionValue];
      handleChange(newValues);
      return;
    }

    const childValues = getChildValues(optionValue);
    const currentState = getSelectionState(optionValue);

    let newValues = [...selectedValues];

    if (childValues.length === 0) {
      // Leaf node - simple toggle
      // IMPORTANT: When selecting a leaf node, ensure we don't include its parent region
      const parentValue = getParentValue(optionValue);
      
      if (currentState === 'full') {
        // Unselecting: remove the leaf node
        newValues = newValues.filter(v => v !== optionValue);
        // If parent was selected and not all siblings are selected, remove parent
        if (parentValue && newValues.includes(parentValue)) {
          const allSiblings = getChildValues(parentValue);
          const selectedSiblings = allSiblings.filter(sibling => newValues.includes(sibling));
          if (selectedSiblings.length < allSiblings.length) {
            newValues = newValues.filter(v => v !== parentValue);
          }
        }
      } else {
        // Selecting: add only the leaf node, never add parent automatically
        if (!newValues.includes(optionValue)) {
          newValues.push(optionValue);
        }
        // Ensure parent is not included unless all its children are selected
        if (parentValue && newValues.includes(parentValue)) {
          const allSiblings = getChildValues(parentValue);
          const selectedSiblings = allSiblings.filter(sibling => newValues.includes(sibling));
          if (selectedSiblings.length < allSiblings.length) {
            newValues = newValues.filter(v => v !== parentValue);
          }
        }
      }
    } else {
      // Parent node - toggle all children
      if (currentState === 'full') {
        // Unselect all children
        newValues = newValues.filter(v => !childValues.includes(v));
        // Also remove the parent itself if it was selected
        newValues = newValues.filter(v => v !== optionValue);
      } else {
        // Select all children (and remove any that were already selected to avoid duplicates)
        newValues = newValues.filter(v => !childValues.includes(v));
        newValues.push(...childValues);
        // Don't add parent - only add children
        // Parent will be added automatically by handleChange if all children are selected
      }
    }

    handleChange([...new Set(newValues)]); // Remove duplicates
  };

  const handleSelectAll = () => {
    // For hierarchical, select all leaf nodes only (to avoid redundancy)
    if (hierarchical) {
      const leafValues = flatOptions.filter(opt => 
        !opt.children || opt.children.length === 0
      ).map(opt => opt.value);
      handleChange(leafValues);
    } else {
      const allValues = options.map(option => option.value);
      handleChange(allValues);
    }
  };

  const handleDeselectAll = () => {
    handleChange([]);
  };

  const toggleExpansion = (nodeValue: string) => {
    const newExpanded = new Set(expandedNodes);
    if (expandedNodes.has(nodeValue)) {
      newExpanded.delete(nodeValue);
    } else {
      newExpanded.add(nodeValue);
    }
    setExpandedNodes(newExpanded);
  };

  const selectedOptions = React.useMemo(() => {
    if (hierarchical) {
      // For hierarchical, show only leaf nodes that are selected
      return flatOptions.filter(opt => 
        selectedValues.includes(opt.value) && (!opt.children || opt.children.length === 0)
      );
    }
    return options.filter(option => selectedValues.includes(option.value));
  }, [options, selectedValues, hierarchical, flatOptions]);

  const allSelected = React.useMemo(() => {
    if (hierarchical) {
      const leafValues = flatOptions.filter(opt => 
        !opt.children || opt.children.length === 0
      ).map(opt => opt.value);
      return leafValues.length > 0 && leafValues.every(val => selectedValues.includes(val));
    }
    return selectedValues.length === options.length;
  }, [selectedValues, options, hierarchical, flatOptions]);

  const noneSelected = selectedValues.length === 0;

  // Render hierarchical item
  const renderHierarchicalItem = (option: HierarchicalOption, index: number) => {
    const hasChildren = option.children && option.children.length > 0;
    const isExpanded = expandedNodes.has(option.value);
    const selectionState = getSelectionState(option.value);
    const level = option.level || 0;

    const getCheckboxProps = () => {
      switch (selectionState) {
        case 'full':
          return { checked: true, indeterminate: false };
        case 'partial':
          return { checked: false, indeterminate: true };
        default:
          return { checked: false, indeterminate: false };
      }
    };

    return (
      <React.Fragment key={option.value}>
        <ListItem
          disablePadding
          sx={{ 
            pl: level * 2,
            bgcolor: selectionState === 'partial' ? '#fafafa' : 'transparent'
          }}
        >
          <ListItemButton
            onClick={() => handleHierarchicalToggle(option.value)}
            dense
            sx={{
              minHeight: 32,
              fontFamily: "'Inter', sans-serif",
              '&:hover': {
                bgcolor: '#fafafa',
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Checkbox
                edge="start"
                {...getCheckboxProps()}
                tabIndex={-1}
                disableRipple
                size="small"
                sx={{
                  color: '#71717a',
                  '&.Mui-checked': {
                    color: '#18181b',
                  },
                  '&.MuiCheckbox-indeterminate': {
                    color: '#18181b',
                  },
                }}
              />
            </ListItemIcon>
            <ListItemText 
              primary={formatChipLabel(option.label, option.value)}
              primaryTypographyProps={{
                variant: 'body2',
                sx: { 
                  color: '#18181b',
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '14px',
                  lineHeight: '20px',
                  fontWeight: hasChildren ? 500 : 400
                }
              }}
            />
            {hasChildren && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpansion(option.value);
                }}
                sx={{ color: '#71717a' }}
              >
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </ListItemButton>
        </ListItem>
        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {option.children!.map((child, childIndex) => 
                renderHierarchicalItem({
                  ...child,
                  level: level + 1,
                  parentValue: option.value
                }, childIndex)
              )}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  // Get all leaf nodes (locations without children) for 3-column display
  const getAllLeafNodes = (opts: HierarchicalOption[]): HierarchicalOption[] => {
    const leafNodes: HierarchicalOption[] = [];
    const traverse = (nodes: HierarchicalOption[]) => {
      nodes.forEach(node => {
        if (!node.children || node.children.length === 0) {
          leafNodes.push(node);
        } else {
          traverse(node.children);
        }
      });
    };
    traverse(opts);
    return leafNodes;
  };

  // Render hierarchical list in 3 columns
  const renderHierarchicalList = () => {
    const leafNodes = getAllLeafNodes(options as HierarchicalOption[]);
    const itemsPerColumn = Math.ceil(leafNodes.length / 3);
    
    return (
      <Box sx={{ 
        display: 'flex',
        gap: 2,
        bgcolor: '#ffffff'
      }}>
        {[0, 1, 2].map(columnIndex => {
          const startIndex = columnIndex * itemsPerColumn;
          const endIndex = Math.min(startIndex + itemsPerColumn, leafNodes.length);
          const columnItems = leafNodes.slice(startIndex, endIndex);
          
          return (
            <Box key={columnIndex} sx={{ flex: 1, minWidth: 0 }}>
              {columnItems.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <Box
                    key={option.value}
                    onClick={() => handleHierarchicalToggle(option.value)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: '#fafafa',
                      }
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      size="small"
                      sx={{
                        color: '#71717a',
                        '&.Mui-checked': {
                          color: '#f97316',
                        },
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#18181b',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '14px',
                        lineHeight: '20px',
                        fontWeight: 400,
                      }}
                    >
                      {formatChipLabel(option.label, option.value)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box sx={{ 
      p: 0, 
      bgcolor: 'transparent', 
      borderRadius: 0, 
      color: '#18181b',
      ...(hierarchical ? {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0
      } : {})
    }}>
      {!hierarchical && (
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            color: '#18181b', 
            mb: 2,
            fontFamily: "'Inter', sans-serif",
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '24px'
          }}
        >
          {title}
        </Typography>
      )}
      
      {hierarchical ? (
        // Hierarchical display - only render the list, title and button handled by parent
        <Box sx={{ 
          flex: 1, 
          minHeight: 0, 
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          // Force scrollbar to always be visible
          '&::-webkit-scrollbar': {
            width: '8px',
            display: 'block',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f4f4f5',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#d4d4d8',
            borderRadius: '4px',
            '&:hover': {
              background: '#a1a1aa',
            },
          },
        }}>
          {renderHierarchicalList()}
        </Box>
      ) : (
        <>
          {allowSelectAll && (
            <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant={allSelected ? "contained" : "outlined"}
                onClick={allSelected ? handleDeselectAll : handleSelectAll}
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '20px',
                  textTransform: 'none',
                  color: allSelected ? '#ffffff' : '#18181b',
                  backgroundColor: allSelected ? '#18181b' : 'transparent',
                  borderColor: '#e4e4e7',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  '&:hover': {
                    borderColor: '#18181b',
                    backgroundColor: allSelected ? '#27272a' : '#fafafa'
                  }
                }}
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </Button>
              {!noneSelected && !allSelected && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#71717a',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '14px',
                    lineHeight: '20px'
                  }}
                >
                  {selectedValues.length} selected
                </Typography>
              )}
            </Box>
          )}
          {/* Traditional autocomplete display - styled to match example */}
          <Box>
          <Autocomplete
            multiple
            options={options}
            getOptionLabel={(option) => formatChipLabel(option.label, option.value)}
            value={selectedOptions}
            onChange={(_, newValue) => {
              handleChange(newValue.map(option => option.value));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selectedValues.length === 0 ? placeholder : ''}
                size="small"
                variant="outlined"
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    color: '#18181b',
                    borderRadius: '6px',
                    border: '1px solid #e4e4e7',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '14px',
                    lineHeight: '20px',
                    minHeight: '40px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e4e4e7',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#18181b',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#18181b',
                      borderWidth: '1px',
                    },
                    '& .MuiInputBase-input': {
                      padding: '8px 12px',
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '14px',
                      lineHeight: '20px',
                      color: '#18181b',
                    },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#71717a',
                    opacity: 1,
                    fontFamily: "'Inter', sans-serif",
                  },
                }}
              />
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => {
                const { key, color, size, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    label={formatChipLabel(option.label, option.value)}
                    {...tagProps}
                    key={option.value}
                    sx={{
                      height: '24px',
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '12px',
                      lineHeight: '16px',
                      fontWeight: 500,
                      backgroundColor: '#f4f4f5',
                      color: '#18181b',
                      border: '1px solid #e4e4e7',
                      borderRadius: '4px',
                      padding: '0 8px',
                      margin: '2px',
                      '& .MuiChip-label': {
                        padding: '0 4px 0 0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      },
                      '& .MuiChip-deleteIcon': {
                        color: '#71717a',
                        fontSize: '14px',
                        width: '16px',
                        height: '16px',
                        margin: '0 0 0 4px',
                        '&:hover': {
                          color: '#18181b',
                        },
                      },
                    }}
                  />
                );
              })
            }
            renderOption={(props, option) => (
              <Box 
                component="li" 
                {...props} 
                sx={{ 
                  color: '#18181b',
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '14px',
                  lineHeight: '20px',
                  padding: '8px 12px',
                  '&:hover': {
                    backgroundColor: '#fafafa',
                  },
                  '&[aria-selected="true"]': {
                    backgroundColor: '#f4f4f5',
                  },
                }}
              >
                <Checkbox
                  checked={selectedValues.includes(option.value)}
                  sx={{
                    color: '#71717a',
                    '&.Mui-checked': {
                      color: '#f97316',
                    },
                  }}
                />
                {formatChipLabel(option.label, option.value)}
              </Box>
            )}
            sx={{ 
              minWidth: 300,
              width: '100%',
            }}
            disabled={disabled}
            disableCloseOnSelect
            ListboxProps={{
              sx: {
                bgcolor: '#ffffff',
                border: '1px solid #e4e4e7',
                borderRadius: '6px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                padding: '4px',
                maxHeight: '300px',
                '& .MuiAutocomplete-option': {
                  borderRadius: '4px',
                  margin: '2px 0',
                },
              },
            }}
            popupIcon={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: '#71717a',
                  fontSize: '16px',
                }}
              >
                ▼
              </Box>
            }
          />
          {selectedValues.length > 0 && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 1,
                color: '#71717a',
                fontFamily: "'Inter', sans-serif",
                fontSize: '12px',
                lineHeight: '16px',
              }}
            >
              {selectedValues.length} {selectedValues.length === 1 ? 'item' : 'items'} selected
            </Typography>
          )}
        </Box>
        </>
      )}
      
      {/* Selection limit message */}
      {maxSelection && selectedValues.length >= maxSelection && (
        <Typography 
          variant="caption" 
          sx={{ 
            color: '#f97316', 
            fontFamily: "'Inter', sans-serif",
            fontSize: '12px',
            lineHeight: '16px',
            fontStyle: 'italic',
            mt: 1,
            display: 'block'
          }}
        >
          Maximum {maxSelection} locations can be selected for optimal chart performance
        </Typography>
      )}
    </Box>
  );
}; 